import { action, computed } from 'mobx'
import { type SerializedFilters } from '@/infra/FiltersViewModel'
import { randomUUID } from '@/utils/uuid-util'
import {
  ProgressionStepOptions,
  StatusOptions,
  WaitingOnOptions,
} from '@/features/jobs/list/jobs-table-filter-values'
import {
  clientOwnedSteps,
  type JobListPerspective,
  proOwnedSteps,
} from './job-list-view'
import { type JobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import {
  type SavedJobFilterStore,
  type StoredSavedJobFilter,
} from './SavedJobFilterStore'

/**
 * A named set of filters, shown as a tab above the jobs list. The search
 * applies them without adding them to the filter bar.
 */
export interface SavedJobFilter {
  /**
   * Stable across roles, so a remembered selection survives the viewer's
   * role resolving late.
   */
  id: string
  name: string
  /**
   * Stored the way the filter bar stores its filters, so they can be shown,
   * edited and shared without knowing each filter's value type. For a tab
   * that starts from a default, only the filters added on top.
   */
  filters: SerializedFilters
  /**
   * The default tab this one starts from, whose filters apply before its
   * own. Kept as a reference so the tab follows changes to the default.
   */
  baseId: string | null
  /**
   * One of the tabs every viewer starts with. Defaults can't be edited, only
   * hidden, deleted or duplicated.
   */
  isDefault: boolean
  /**
   * Whether the viewer hid this tab. Hidden tabs stay in Display, to show
   * again later.
   */
  isHidden: boolean
}

/**
 * What a default tab shows, in words, since its filters alone undersell it.
 */
export interface DefaultSavedJobFilterDescription {
  text: string
  /**
   * The progress steps that decide whose turn a job is, when that's what the
   * tab is about.
   */
  steps: string[]
}

/**
 * Changes to one of the viewer's own tabs.
 */
export interface SavedJobFilterChanges {
  name?: string
  filters?: SerializedFilters
}

/**
 * The ID of a tab opened from a shared link.
 */
export const sharedSavedFilterId = 'shared'

/**
 * The longest a tab name can be, so tabs stay readable in the row.
 */
export const maxSavedJobFilterNameLength = 40

/**
 * Trims a tab name and cuts it to {@link maxSavedJobFilterNameLength}.
 */
export function cleanSavedJobFilterName(name: string) {
  return name.trim().slice(0, maxSavedJobFilterNameLength).trim()
}

/**
 * How many tabs of their own a viewer can have, on top of the defaults. The
 * same tabs show in the much narrower job details sidebar.
 */
export const maxOwnSavedJobFilters = 10

interface DefaultSavedJobFilter {
  id: string
  name: string
  filters: SerializedFilters
  description: DefaultSavedJobFilterDescription
}

/**
 * The tabs every viewer starts with. Admins get the same tabs as pros, named
 * for someone watching both sides.
 */
export function defaultSavedJobFilters(
  perspective: JobListPerspective,
): DefaultSavedJobFilter[] {
  const isPro = perspective === 'Pro'
  const waitingOn = (value: string) =>
    JSON.stringify(WaitingOnOptions.filter((o) => o.value === value))

  return [
    {
      id: 'waiting-on-pro',
      name: isPro ? 'Needs you' : 'Waiting on pro',
      filters: { waitingOn: waitingOn('Provider') },
      description: {
        text: `Jobs in progress where it's ${isPro ? 'your' : 'the pro’s'} turn. That's decided by the job's progress step, and falls back to the job's own waiting-on when the step doesn't say.`,
        steps: proOwnedSteps.map(stepTitle),
      },
    },
    {
      id: 'unread',
      name: 'Unread',
      filters: { unread: JSON.stringify(true) },
      description: {
        text: 'Jobs with messages or updates you haven’t seen.',
        steps: [],
      },
    },
    {
      id: 'waiting-on-client',
      name: 'Waiting on client',
      filters: { waitingOn: waitingOn('Client') },
      description: {
        text: "Jobs where it's the client's turn: still gathering their information, or in progress at a step that needs them.",
        steps: clientOwnedSteps.map(stepTitle),
      },
    },
    {
      id: 'active',
      name: 'Active',
      filters: {
        jobStatus: JSON.stringify(
          StatusOptions.filter((o) => o.value !== 'Closed'),
        ),
      },
      description: { text: 'Every job that isn’t closed.', steps: [] },
    },
    {
      id: 'closed',
      name: 'Closed',
      filters: {
        jobStatus: JSON.stringify(
          StatusOptions.filter((o) => o.value === 'Closed'),
        ),
      },
      description: { text: 'Jobs that are closed.', steps: [] },
    },
    {
      id: 'all',
      name: 'All',
      filters: {},
      description: { text: 'Every job you can see.', steps: [] },
    },
  ]
}

/**
 * A default tab's description, or `null` for any other tab.
 */
export function describeDefaultSavedJobFilter(
  perspective: JobListPerspective,
  id: string,
): DefaultSavedJobFilterDescription | null {
  return (
    defaultSavedJobFilters(perspective).find((d) => d.id === id)?.description ??
    null
  )
}

function stepTitle(step: JobProgressionStepStatus) {
  // The filter's options drop the "Progression" prefix some steps carry.
  const option = ProgressionStepOptions.find(
    (o) => o.value === step || `Progression${o.value}` === step,
  )
  return option?.title ?? step
}

/**
 * The viewer's tabs for one perspective: the defaults they kept, plus their
 * own, in their order.
 */
export class SavedJobFilters {
  constructor(
    private readonly _store: SavedJobFilterStore,
    private readonly _getPerspective: () => JobListPerspective,
  ) {}

  /**
   * Every tab in the viewer's order, hidden ones included and deleted
   * defaults left out.
   */
  @computed
  get all(): SavedJobFilter[] {
    const defaults = defaultSavedJobFilters(this._getPerspective())
    return this._entries().flatMap((entry): SavedJobFilter[] => {
      const byDefault = defaults.find((d) => d.id === entry.id)
      if (byDefault) {
        return entry.isDeleted
          ? []
          : [
              {
                id: byDefault.id,
                name: byDefault.name,
                filters: byDefault.filters,
                baseId: null,
                isDefault: true,
                isHidden: !!entry.isHidden,
              },
            ]
      }
      if (!entry.name || !entry.filters) {
        return []
      }
      return [
        {
          id: entry.id,
          name: entry.name,
          filters: entry.filters,
          baseId: defaults.some((d) => d.id === entry.baseId)
            ? (entry.baseId ?? null)
            : null,
          isDefault: false,
          isHidden: !!entry.isHidden,
        },
      ]
    })
  }

  /**
   * The tabs shown.
   */
  @computed
  get visible(): SavedJobFilter[] {
    return this.all.filter((f) => !f.isHidden)
  }

  /**
   * Defaults the viewer deleted, which they can add back.
   */
  @computed
  get deletedDefaults(): Pick<SavedJobFilter, 'id' | 'name'>[] {
    const defaults = defaultSavedJobFilters(this._getPerspective())
    return this._entries().flatMap((entry) => {
      const byDefault = defaults.find((d) => d.id === entry.id)
      return byDefault && entry.isDeleted
        ? [{ id: byDefault.id, name: byDefault.name }]
        : []
    })
  }

  /**
   * Whether another tab fits under {@link maxOwnSavedJobFilters}.
   */
  @computed
  get canAdd() {
    return this.all.filter((f) => !f.isDefault).length < maxOwnSavedJobFilters
  }

  /**
   * The filters a tab applies: its default's, then its own. A tab keeps
   * following its default after the default is hidden or deleted.
   */
  filtersOf(
    tab: Pick<SavedJobFilter, 'filters' | 'baseId'>,
  ): SerializedFilters {
    return { ...this.defaultOf(tab.baseId)?.filters, ...tab.filters }
  }

  /**
   * A default tab by ID, whether or not the viewer still has it.
   */
  defaultOf(
    id: string | null,
  ): Pick<SavedJobFilter, 'id' | 'name' | 'filters'> | null {
    return (
      defaultSavedJobFilters(this._getPerspective()).find((d) => d.id === id) ??
      null
    )
  }

  /**
   * Adds one of the viewer's own tabs after `afterId`, or at the end.
   *
   * @returns The new tab, or `null` when there's no room.
   */
  @action.bound
  add(
    name: string,
    filters: SerializedFilters,
    baseId: string | null,
    afterId?: string,
  ): SavedJobFilter | null {
    return this._add(name, filters, baseId, afterId, false)
  }

  /**
   * Adds a tab like {@link add} that isn't stored until it's updated, so it
   * goes away with the page unless saved.
   *
   * @returns The new tab, or `null` when there's no room.
   */
  @action.bound
  addDraft(
    name: string,
    filters: SerializedFilters,
    baseId: string | null,
    afterId?: string,
  ): SavedJobFilter | null {
    return this._add(name, filters, baseId, afterId, true)
  }

  /**
   * Changes one of the viewer's own tabs, which stores a draft. Defaults
   * can't change.
   */
  @action.bound
  update(id: string, changes: SavedJobFilterChanges) {
    if (this._isDefault(id)) {
      return
    }
    const name =
      changes.name === undefined
        ? undefined
        : cleanSavedJobFilterName(changes.name)
    this._save(
      this._entries().map((e) =>
        e.id === id
          ? { ...e, ...changes, ...(name ? { name } : {}), isDraft: undefined }
          : e,
      ),
    )
  }

  /**
   * Hides a tab or shows it again, in its place.
   */
  @action.bound
  setHidden(id: string, isHidden: boolean) {
    this._save(
      this._entries().map((e) =>
        e.id === id ? { ...e, isHidden: isHidden || undefined } : e,
      ),
    )
  }

  /**
   * Deletes a tab. A deleted default keeps its place, so adding it back
   * returns it there.
   */
  @action.bound
  delete(id: string) {
    this._save(
      this._isDefault(id)
        ? this._entries().map((e) =>
            e.id === id ? { id, isDeleted: true } : e,
          )
        : this._entries().filter((e) => e.id !== id),
    )
  }

  /**
   * Adds a deleted default back, shown, in its old place.
   */
  @action.bound
  addBack(id: string) {
    this._save(this._entries().map((e) => (e.id === id ? { id } : e)))
  }

  /**
   * Puts every default back in its original order, shown, with the viewer's
   * own tabs after them as they were.
   */
  @action.bound
  resetDefaults() {
    const defaults = defaultSavedJobFilters(this._getPerspective())
    this._save([
      ...defaults.map((d): StoredSavedJobFilter => ({ id: d.id })),
      ...this._entries().filter((e) => !this._isDefault(e.id)),
    ])
  }

  /**
   * Moves a tab in front of another, or to the end when `beforeId` is
   * `null`.
   */
  @action.bound
  move(id: string, beforeId: string | null) {
    if (id === beforeId) {
      return
    }
    const entries = this._entries()
    const from = entries.findIndex((e) => e.id === id)
    if (from === -1) {
      return
    }
    const [moved] = entries.splice(from, 1)
    const to = entries.findIndex((e) => e.id === beforeId)
    entries.splice(to === -1 ? entries.length : to, 0, moved)
    this._save(entries)
  }

  private _add(
    name: string,
    filters: SerializedFilters,
    baseId: string | null,
    afterId: string | undefined,
    isDraft: boolean,
  ): SavedJobFilter | null {
    if (!this.canAdd) {
      return null
    }
    const id = `custom-${randomUUID()}`
    const entries = this._entries()
    const index = entries.findIndex((e) => e.id === afterId)
    entries.splice(index === -1 ? entries.length : index + 1, 0, {
      id,
      name: cleanSavedJobFilterName(name),
      filters,
      baseId: baseId ?? undefined,
      isDraft: isDraft || undefined,
    })
    this._save(entries)
    return this.all.find((f) => f.id === id) ?? null
  }

  private _isDefault(id: string) {
    return this.defaultOf(id) !== null
  }

  /**
   * Every tab as stored, in order, deleted defaults included. Defaults added
   * after the viewer last saved go at the end.
   */
  private _entries(): StoredSavedJobFilter[] {
    const stored = this._store.entriesFor(this._getPerspective())
    const missing = defaultSavedJobFilters(this._getPerspective())
      .filter((d) => !stored.some((s) => s.id === d.id))
      .map((d): StoredSavedJobFilter => ({ id: d.id }))
    return [...stored.map((e) => ({ ...e })), ...missing]
  }

  private _save(entries: StoredSavedJobFilter[]) {
    this._store.save(this._getPerspective(), entries)
  }
}
